import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Standard PDF fonts are WinAnsi: normalise special spaces/characters.
const clean = (s) => String(s ?? "").replace(/[\u202F\u00A0]/g, " ").replace(/→/g, "->");
const eur = (v) => clean(Number(v || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" }));
const hm = (h) => {
  const total = Math.round(Number(h || 0) * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return hh === 0 ? `${mm} min` : `${hh}h${String(mm).padStart(2, "0")}`;
};
const pct = (v) => `${Number(v || 0).toLocaleString("pt-PT", { maximumFractionDigits: 1 })}%`;
const dmy = (d) => { if (!d) return "—"; const [y, m, dd] = d.split("-"); return `${dd}/${m}/${y}`; };
const GROUP = { day: "Dia", week: "Semana", month: "Mês" };
const PLURAL = { day: "dia(s)", week: "semana(s)", month: "mês(es)" };
const TEAL = [15, 118, 110];

export function rangeLabel(range) {
  if (!range?.start && !range?.end) return "Todo o período";
  return `${range.start ? dmy(range.start) : "início"} a ${range.end ? dmy(range.end) : "hoje"}`;
}

let logoCache = null;
async function loadLogo() {
  if (logoCache) return logoCache;
  try {
    const res = await fetch(`${process.env.PUBLIC_URL || ""}/samidel-logo-white.png`);
    const blob = await res.blob();
    logoCache = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    logoCache = null;
  }
  return logoCache;
}

async function newDoc(title, subtitle) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const logo = await loadLogo();
  // Brand block — same as the app: white logo on slate-900 rounded box.
  doc.setFillColor(15, 23, 42);
  doc.roundedRect(14, 8, 40, 13, 2.5, 2.5, "F");
  if (logo) doc.addImage(logo, "PNG", 17, 10.5, 34, 34 * (67 / 276));
  const x = 60;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(20, 20, 20);
  doc.text(clean(title), x, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(clean(subtitle), x, 19.5);
  doc.text(clean("Gestão de corte · Rex 50 ES"), 283, 13, { align: "right" });
  doc.text(clean(`Gerado em ${new Date().toLocaleString("pt-PT")}`), 283, 18, { align: "right" });
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.6);
  doc.line(14, 24, 283, 24);
  return doc;
}

function footer(doc, title) {
  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i += 1) {
    doc.setPage(i);
    if (i > 1) {
      // Small repeated brand header on following pages.
      doc.setFillColor(15, 23, 42);
      doc.roundedRect(14, 6, 26, 8.5, 1.8, 1.8, "F");
      if (logoCache) doc.addImage(logoCache, "PNG", 16, 7.4, 22, 22 * (67 / 276));
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(clean(title || ""), 44, 11.5);
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.4);
      doc.line(14, 16.5, 283, 16.5);
    }
    doc.setFontSize(7.5);
    doc.setTextColor(140, 140, 140);
    const note = doc.splitTextToSize(clean("Custo fixo do dia ÷ horas reais de OF (marcadas + entre marcadas, que contam para a OF seguinte; 1.º corte desde as 08:00). Regras da análise diária: pausas previstas retiradas das marcadas e do entre marcadas (não ao sábado); horas extra contam, exceto a espera > 60 min antes do turno extra. Desgaste e manutenção por hora de corte real."), 235);
    doc.text(note, 14, 200);
    doc.text(`Página ${i} de ${n}`, 283, 203, { align: "right" });
  }
}

const head = { fillColor: TEAL, textColor: 255, fontStyle: "bold", fontSize: 8 };
const body = { fontSize: 8, cellPadding: 1.6 };
const totalRow = { fontStyle: "bold", fillColor: [238, 242, 241] };

function para(doc, y, lines) {
  doc.setFontSize(8.5);
  doc.setTextColor(70, 70, 70);
  for (const l of lines) {
    const parts = doc.splitTextToSize(clean(l), 268);
    doc.text(parts, 14, y);
    y += parts.length * 4;
  }
  return y;
}

function sectionTitle(doc, y, text) {
  if (y > 180) { doc.addPage(); y = 24; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  doc.text(clean(text), 14, y);
  doc.setFont("helvetica", "normal");
  return y + 5;
}

/** Day / week / month report with the split by OF of every period. */
export async function exportPeriodsPdf({ groups, groupBy, range, rates, filename }) {
  const title = `Custos por ${GROUP[groupBy].toLowerCase()}`;
  const doc = await newDoc(title, `Período: ${rangeLabel(range)} · ${groups.length} ${PLURAL[groupBy]}`);
  const varRate = (rates?.wear_per_hour || 0) + (rates?.maintenance_per_hour || 0);
  const tot = groups.reduce((a, g) => ({
    m: a.m + g.cost_modelagem, me: a.me + g.cost_mesa, e: a.e + g.cost_electricity, d: a.d + (g.cost_depreciation || 0),
    p: a.p + g.cost_punctual, f: a.f + g.fixed_cost, v: a.v + g.variable_cost, h: a.h + g.of_hours, c: a.c + (g.cuts_count || 0),
  }), { m: 0, me: 0, e: 0, d: 0, p: 0, f: 0, v: 0, h: 0, c: 0 });

  autoTable(doc, {
    startY: 27,
    head: [[GROUP[groupBy], "Modelagem", "Mesa corte", "Eletric.", "Depreciação", "Pontuais", "Custo fixo", "Horas OF", "€/hora", "Desg.+manut.", "Total", "Cortes"]],
    body: [
      ...groups.map((g) => [
        clean(`${g.label}${g.sub ? ` (${g.sub})` : ""}${groupBy !== "day" ? ` · ${g.days.length} dia(s)` : ""}`),
        eur(g.cost_modelagem), eur(g.cost_mesa), eur(g.cost_electricity), eur(g.cost_depreciation), eur(g.cost_punctual),
        eur(g.fixed_cost), hm(g.of_hours), eur(g.cost_per_hour), eur(g.variable_cost), eur(g.fixed_cost + g.variable_cost), g.cuts_count ?? "",
      ]),
      [{ content: "Total", styles: totalRow }, ...[eur(tot.m), eur(tot.me), eur(tot.e), eur(tot.d), eur(tot.p), eur(tot.f), hm(tot.h),
        groups.length === 1 ? eur(groups[0].cost_per_hour) : (tot.h ? `${eur(tot.f / tot.h)} (média)` : "—"), eur(tot.v), eur(tot.f + tot.v), tot.c].map((c) => ({ content: c, styles: totalRow }))],
    ],
    headStyles: head, margin: { top: 20, bottom: 16 }, styles: body,
    columnStyles: Object.fromEntries([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => [i, { halign: "right" }])),
  });

  let y = doc.lastAutoTable.finalY + 8;
  for (const g of groups) {
    y = sectionTitle(doc, y, `${g.label}${g.sub ? ` · ${g.sub}` : ""} — divisão por OF`);
    y = para(doc, y, [
      groupBy === "day"
        ? `Custo fixo do dia ${eur(g.fixed_cost)} ÷ ${hm(g.of_hours)} reais de OF (${hm(g.own_hours)} de marcadas + ${hm(g.between_hours)} entre marcadas) = ${eur(g.cost_per_hour)}/hora.`
        : `${g.days.length} dia(s): cada dia é calculado com o seu €/hora e os valores são a soma desses dias.`,
      `Desgaste + manutenção: ${eur(varRate)} por hora de corte real.`,
    ]);
    autoTable(doc, {
      startY: y + 1,
      head: [["OF", ...(groupBy !== "day" ? ["Dias"] : []), "Cortes", "Horas OF", "Marcadas + entre", "% do tempo", "Parte do custo fixo", "Desg.+manut.", "Custo OF"]],
      body: [
        ...g.orders.map((o) => [
          clean(o.name), ...(groupBy !== "day" ? [o.days] : []), o.cuts_count, hm(o.hours), `${hm(o.own_hours)} + ${hm(o.between_hours)}`,
          pct(o.share_pct), eur(o.cost_fixed), eur(o.cost_variable), eur(o.cost_total),
        ]),
        [{ content: "Total", styles: totalRow }, ...(groupBy !== "day" ? [{ content: "", styles: totalRow }] : []),
          ...[g.cuts_count ?? "", hm(g.of_hours), "", "100%", eur(g.fixed_cost), eur(g.variable_cost), eur(g.fixed_cost + g.variable_cost)].map((c) => ({ content: c, styles: totalRow }))],
      ],
      headStyles: head, margin: { top: 20, bottom: 16 }, styles: body,
      columnStyles: { 0: { cellWidth: 80 } },
      didParseCell: (d) => { if (d.column.index > 0) d.cell.styles.halign = "right"; },
    });
    y = doc.lastAutoTable.finalY + 8;
  }
  footer(doc, title);
  doc.save(filename || `custos-${groupBy}-${range?.start || "inicio"}-${range?.end || "fim"}.pdf`);
}

function orderDetailTable(doc, y, o, periods, groupBy) {
  autoTable(doc, {
    startY: y,
    head: [[GROUP[groupBy], ...(groupBy !== "day" ? ["Dias"] : ["€/hora do dia"]), "Cortes", "Horas OF", "Marcadas + entre", "Parte do custo fixo", "Desg.+manut.", "Custo OF"]],
    body: [
      ...periods.map((r) => [
        clean(`${r.label}${r.sub ? ` (${r.sub})` : ""}`), groupBy !== "day" ? r.days : eur(r.cph), r.cuts_count, hm(r.hours),
        `${hm(r.own_hours)} + ${hm(r.between_hours)}`, eur(r.cost_fixed), eur(r.cost_variable), eur(r.cost_total),
      ]),
      [{ content: "Total", styles: totalRow }, ...["", o.cuts_count, hm(o.hours), `${hm(o.own_hours)} + ${hm(o.between_hours)}`,
        eur(periods.reduce((a, r) => a + r.cost_fixed, 0)), eur(periods.reduce((a, r) => a + r.cost_variable, 0)),
        eur(periods.reduce((a, r) => a + r.cost_total, 0))].map((c) => ({ content: c, styles: totalRow }))],
    ],
    headStyles: head, margin: { top: 20, bottom: 16 }, styles: body,
    didParseCell: (d) => { if (d.column.index > 0) d.cell.styles.halign = "right"; },
  });
  return doc.lastAutoTable.finalY;
}

function orderComponents(doc, y, o) {
  autoTable(doc, {
    startY: y,
    head: [["Modelagem", "Mesa corte", "Eletricidade", "Depreciação", "Pontuais", "Desgaste", "Manutenção", "Custo da OF"]],
    body: [[eur(o.cost_modelagem), eur(o.cost_mesa), eur(o.cost_electricity), eur(o.cost_depreciation), eur(o.cost_punctual),
      eur(o.cost_wear), eur(o.cost_maintenance), { content: eur(o.cost_total), styles: { fontStyle: "bold" } }]],
    headStyles: head, margin: { top: 20, bottom: 16 }, styles: { ...body, halign: "right" },
  });
  return doc.lastAutoTable.finalY;
}

/** Single OF report. */
export async function exportOrderPdf({ order: o, periods, groupBy, range }) {
  const doc = await newDoc(`Custo da ${o.name}`, `Período: ${rangeLabel(range)} · ${o.cuts_count} cortes · ${o.days_count} dia(s) · ${hm(o.hours)} de OF (${hm(o.own_hours)} de marcadas + ${hm(o.between_hours)} entre marcadas)`);
  let y = orderComponents(doc, 27, o) + 8;
  y = sectionTitle(doc, y, `Custo por ${GROUP[groupBy].toLowerCase()}`);
  orderDetailTable(doc, y, o, periods, groupBy);
  footer(doc, `Custo da ${o.name}`);
  doc.save(`custo-${o.key.replace(/[^a-z0-9.]+/gi, "_")}-${range?.start || "inicio"}-${range?.end || "fim"}.pdf`);
}

/** All OFs: summary list + (optional) detail of each OF by period. */
export async function exportOrdersPdf({ orders, periodsOf, groupBy, range, withDetail, typeLabel }) {
  const title = "Custo por ordem de fabrico";
  const doc = await newDoc(title, `Período: ${rangeLabel(range)} · ${orders.length} ${typeLabel ? `(${typeLabel})` : "OF"}`);
  const sum = (k) => orders.reduce((a, o) => a + Number(o[k] || 0), 0);
  autoTable(doc, {
    startY: 27,
    head: [["OF", "Cortes", "Dias", "Horas OF", "Modelagem", "Mesa corte", "Eletric.", "Depreciação", "Pontuais", "Desgaste", "Manut.", "Custo OF"]],
    body: [
      ...orders.map((o) => [clean(o.name), o.cuts_count, o.days_count, hm(o.hours), eur(o.cost_modelagem), eur(o.cost_mesa), eur(o.cost_electricity),
        eur(o.cost_depreciation), eur(o.cost_punctual), eur(o.cost_wear), eur(o.cost_maintenance), eur(o.cost_total)]),
      [{ content: "Total", styles: totalRow }, ...[sum("cuts_count"), "", hm(sum("hours")), eur(sum("cost_modelagem")), eur(sum("cost_mesa")),
        eur(sum("cost_electricity")), eur(sum("cost_depreciation")), eur(sum("cost_punctual")), eur(sum("cost_wear")), eur(sum("cost_maintenance")),
        eur(sum("cost_total"))].map((c) => ({ content: c, styles: totalRow }))],
    ],
    headStyles: head, margin: { top: 20, bottom: 16 }, styles: body,
    columnStyles: { 0: { cellWidth: 62 } },
    didParseCell: (d) => { if (d.column.index > 0) d.cell.styles.halign = "right"; },
  });
  if (withDetail) {
    let y = doc.lastAutoTable.finalY + 8;
    for (const o of orders) {
      y = sectionTitle(doc, y, `${o.name} — ${eur(o.cost_total)} · por ${GROUP[groupBy].toLowerCase()}`);
      y = orderDetailTable(doc, y, o, periodsOf(o), groupBy) + 8;
    }
  }
  footer(doc, title);
  doc.save(`custos-ofs-${range?.start || "inicio"}-${range?.end || "fim"}.pdf`);
}
